import { useState } from 'react';
import { useAuth } from './auth/AuthContext';
import { roleLabel } from './api/auth';
import { useMaintenance } from './api/maintenance';
import LoginScreen from './screens/LoginScreen';
import BillingScreen from './screens/BillingScreen';
import TodaysBillsScreen from './screens/TodaysBillsScreen';
import HistoryScreen from './screens/HistoryScreen';
import StoreStockScreen from './screens/StoreStockScreen';
import OnlineOrdersScreen from './screens/OnlineOrdersScreen';
import AddBookScreen from './screens/AddBookScreen';

type Tab = 'billing' | 'today' | 'history' | 'stock' | 'addbook' | 'online';

// `needs` is the switch on the admin panel's Roles & Permissions screen that
// opens each tab; holding any one of them is enough.
const TABS: { key: Tab; label: string; needs: string[] }[] = [
  { key: 'billing', label: 'New sale', needs: ['pos.sell'] },
  { key: 'today', label: "Today's bills", needs: ['pos.bills.view'] },
  { key: 'history', label: 'History', needs: ['pos.bills.view'] },
  { key: 'stock', label: 'Shop stock', needs: ['pos.sell'] },
  { key: 'addbook', label: 'Add / edit book', needs: ['products.create', 'products.edit'] },
  { key: 'online', label: 'Online orders', needs: ['pos.online_orders'] },
];

/**
 * Six screens and a sign-out button. A router would be four more dependencies
 * for a terminal that never navigates anywhere else.
 */
export default function App() {
  const { user, ready, can, signOut } = useAuth();
  const [wantedTab, setTab] = useState<Tab>('billing');
  const tabs = TABS.filter((t) => can(...t.needs));
  // The tab last chosen, unless its switch has since been turned off — then
  // the first one this role still has.
  const tab: Tab | null = tabs.some((t) => t.key === wantedTab)
    ? wantedTab
    : (tabs[0]?.key ?? null);
  const allowed = (key: Tab) => tabs.some((t) => t.key === key);
  // Add book is not mounted until it is first opened — it loads every author
  // in the catalogue, which a till that only bills never needs.
  const [addBookOpened, setAddBookOpened] = useState(false);
  // A book picked on Shop stock to be corrected. `n` moves on every ask, so
  // picking the same book a second time opens it again.
  const [editRequest, setEditRequest] = useState<{ id: string; n: number } | null>(null);

  function editBook(id: string) {
    setEditRequest((prev) => ({ id, n: (prev?.n ?? 0) + 1 }));
    setAddBookOpened(true);
    setTab('addbook');
  }
  // Changes each time maintenance ends; see the keyed panels below.
  const { resumed } = useMaintenance();

  if (!ready) {
    return (
      <div className="boot">
        <span className="spinner" aria-hidden="true" />
        <p>Starting the till…</p>
      </div>
    );
  }

  if (!user) return <LoginScreen />;

  return (
    <div className="app">
      <header className="topbar">
        <div className="brand">
          <strong>Blossom Bookfair</strong>
          <span>Billing counter</span>
        </div>

        <nav className="tabs" aria-label="Sections">
          {tabs.map((t) => (
            <button
              key={t.key}
              type="button"
              className={tab === t.key ? 'tab tab-active' : 'tab'}
              onClick={() => {
                setTab(t.key);
                if (t.key === 'addbook') setAddBookOpened(true);
              }}
            >
              {t.label}
            </button>
          ))}
        </nav>

        <div className="who">
          {/* Whose till this is. On a counter PC that several people share
              across a day, the name and the job on screen are what stop a bill
              being rung up under somebody else's account without anyone
              noticing. */}
          <span className="who-name">{user.fullName}</span>
          <span className="who-role">{roleLabel(user.role)}</span>
          {/* Icon only, but it still has to announce itself: `aria-label` for a
              screen reader and `title` for the staff member who does not recognise
              the glyph and hovers to find out. */}
          <button
            type="button"
            className="icon-btn"
            onClick={signOut}
            aria-label="Sign out"
            title="Sign out"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <path d="M16 17l5-5-5-5" />
              <path d="M21 12H9" />
            </svg>
          </button>
        </div>
      </header>

      <main className="main">
        {tab === null && (
          <div className="boot">
            <p>
              Your role has not been given anything on the till. Ask the Super
              Admin to switch it on under Roles &amp; Permissions.
            </p>
          </div>
        )}
        {/* Billing stays mounted so a half-built bill survives a peek at the
            day's takings — losing a scanned bill to a stray tab press would be
            a genuinely expensive mistake at a busy counter. */}
        {allowed('billing') && (
          <div hidden={tab !== 'billing'} className="tab-panel">
            <BillingScreen active={tab === 'billing'} resumed={resumed} />
          </div>
        )}
        {/* The rest mount on demand and refetch when reopened, so the numbers
            are never a stale snapshot from earlier in the shift. Keyed on
            `resumed` for the same reason: when maintenance ends they mount
            afresh and load again, instead of sitting on whatever error the
            503 left behind. */}
        {tab === 'today' && (
          <div className="tab-panel" key={`today-${resumed}`}>
            <TodaysBillsScreen />
          </div>
        )}
        {tab === 'history' && (
          <div className="tab-panel" key={`history-${resumed}`}>
            <HistoryScreen />
          </div>
        )}
        {tab === 'stock' && (
          <div className="tab-panel" key={`stock-${resumed}`}>
            <StoreStockScreen onEdit={editBook} />
          </div>
        )}
        {/* Stays mounted once opened, like billing and for the same reason: a
            half-filled book should survive a look at another tab. */}
        {(addBookOpened || tab === 'addbook') && allowed('addbook') && (
          <div hidden={tab !== 'addbook'} className="tab-panel">
            <AddBookScreen active={tab === 'addbook'} editRequest={editRequest} />
          </div>
        )}
        {tab === 'online' && (
          <div className="tab-panel" key={`online-${resumed}`}>
            <OnlineOrdersScreen />
          </div>
        )}
      </main>
    </div>
  );
}
